import React, { useState } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Plus } from "lucide-react";
import { handlerStyle } from "../common/commonData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  codeNodes,
  customNodes,
  dataTransmission,
  logicNodes,
  lsNodes,
} from "../common/nodeTemplates";
import { GeneralContext } from "@services/providers/GeneralProvider";

interface AddButtonNodeData {
  onAddNode?: (nodeType: string) => void;
  loopNodeId?: string;
}

const AddButtonNode: React.FC<NodeProps<AddButtonNodeData>> = ({ data }) => {
  const [showNodeDialog, setShowNodeDialog] = useState(false);
  const { isAdminUser } = React.useContext(GeneralContext);

  const hasNodePermission = (node: any) => {
    if (!node?.permission) {
      return true;
    }

    if (node.permission === "isAdmin") {
      return isAdminUser;
    }

    if (node.permission === "isUser") {
      return !isAdminUser;
    }

    return true;
  };

  const handleAddNode = (nodeType: string) => {
    data?.onAddNode?.(nodeType);
    setShowNodeDialog(false);
  };

  const renderNodeItem = (node: any) => {
    const Icon = node.icon;

    return (
      <button
        key={node.type}
        onClick={() => handleAddNode(node.type)}
        className="group relative flex flex-col items-center justify-center gap-2 p-4 w-[140px]
                   rounded-2xl border bg-background/60 backdrop-blur-md
                   transition-all duration-300
                   hover:-translate-y-1 hover:shadow-xl hover:scale-[1.03]
                   active:scale-[0.98]"
        style={{
          borderColor: node.borderColor,
          boxShadow: `0 0 0 1px ${node.shadowColor}20`,
        }}
      >
        {/* Glow background */}
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition"
          style={{
            background: `radial-gradient(circle at top, ${node.borderColor}20, transparent 70%)`,
          }}
        />

        <Icon
          color={node.borderColor}
          size={26}
          className={`relative z-10 transition-transform group-hover:scale-110  ${node.header == "If Else" ? "rotate-90" : ""} `}
        />

        <div
          className="text-xs font-semibold text-center relative z-10"
          style={{ color: node.borderColor }}
        >
          {node.header}
        </div>
      </button>
    );
  };

  return (
    <>
      {/* ADD BUTTON NODE */}
      <div
        className="relative bg-background border-4 border-background shadow-2xl rounded-2xl p-4 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform group"
        style={{
          backgroundColor: "var(--light-gray)",
          width: "140px",
          height: "80px",
        }}
        onClick={() => setShowNodeDialog(true)}
        title="Add node to loop"
      >
        {/* Input Handle - Right side */}
        <Handle
          type="target"
          position={Position.Right}
          id="input"
          className={handlerStyle}
          style={{ right: "-8px" }}
        />
        {/* Output Handle - Left side */}
        <Handle
          type="source"
          position={Position.Left}
          id="output"
          className={handlerStyle}
          style={{ left: "-8px" }}
        />
        <Plus
          className="h-6 w-6 text-muted-foreground font-bold"
          strokeWidth={3}
        />
        <span className="text-muted-foreground font-bold ml-1">Add Node</span>
      </div>

      {/* DIALOG */}
      <Dialog open={showNodeDialog} onOpenChange={setShowNodeDialog}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto
                     bg-gradient-to-b from-background/95 to-background
                     backdrop-blur-xl"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Insert Node into Loop
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Choose a node type to extend your workflow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 p-3 border rounded-lg">
            {/* SECTION */}
            {[
              {
                title: "Action Nodes",
                list: customNodes.filter(
                  (node) =>
                    node.type !== "connectFlow" && hasNodePermission(node),
                ),
              },
              {
                title: "Linkshadow Nodes",
                list: lsNodes.filter(
                  (node) =>
                    node.type !== "savelogs" &&
                    node.type !== "savedatalist" &&
                    hasNodePermission(node),
                ),
              },
              {
                title: "Logic Nodes",
                list: logicNodes.filter((node) => hasNodePermission(node)),
              },
              {
                title: "Data Transmission",
                list: dataTransmission.filter((node) =>
                  hasNodePermission(node),
                ),
              },
              {
                title: "Code Nodes",
                list: codeNodes.filter((node) => hasNodePermission(node)),
              },
            ].map((section) =>
              section.list?.length ? (
                <div key={section.title}>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                    {section.title}
                  </h3>

                  <div className="flex flex-wrap gap-4">
                    {section.list.map(renderNodeItem)}
                  </div>
                </div>
              ) : null,
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddButtonNode;
